import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AdminGuard } from 'app/core/auth/guards/admin.guard';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/dashboard'
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed-in user to the '/dashboard'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unauthorized', loadChildren: () => import('app/modules/auth/unauthorized/unauthorized.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
        ]
    },

    // Public exhibitor order form (token-based access, no auth guard)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'order-form', loadChildren: () => import('app/modules/order-form/order-form.routes') },
        ]
    },
    // Authenticated application area.
    //
    // All feature areas share a SINGLE LayoutComponent + resolver entry on purpose.
    // The Angular router only reuses a route's component when the same route config
    // stays activated; splitting these into sibling entries that each re-declare
    // `component: LayoutComponent` makes the router tear down and rebuild the whole
    // layout when navigating across groups. That would reinitialise the toolbar,
    // the vertical navigation and the projected <notifications> component (dropping
    // its live SignalR subscription) and re-run initialDataResolver on every such
    // navigation. Keeping one entry instantiates the layout once and keeps it alive.
    {
        path: '',
        canActivate: [AdminGuard],
        canActivateChild: [AdminGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            // `data.breadcrumb` declares the root crumb for each feature area; the
            // per-entity crumbs beneath it live in each feature's own routes file.
            { path: 'events', data: { breadcrumb: 'Events' }, loadChildren: () => import('app/modules/events/event.routes') },
            { path: 'supplier-reports', data: { breadcrumb: 'Supplier Report' }, loadChildren: () => import('app/modules/supplier-reports/supplier-report.routes') },
            { path: 'suppliers', data: { breadcrumb: 'Suppliers' }, loadChildren: () => import('app/modules/suppliers/supplier.routes') },
            { path: 'product-categories', data: { breadcrumb: 'Product Categories' }, loadChildren: () => import('app/modules/product-categories/product-category.routes') },
            { path: 'price-config', data: { breadcrumb: 'Price Config' }, loadChildren: () => import('app/modules/price-config/price-config.routes') },
            { path: 'exhibitors', data: { breadcrumb: 'Exhibitors' }, loadChildren: () => import('app/modules/exhibitors/exhibitor.routes') },
            { path: 'sales-people', data: { breadcrumb: 'Sales People' }, loadChildren: () => import('app/modules/sales-people/sales-person.routes') },
            { path: 'packages', data: { breadcrumb: 'Packages' }, loadChildren: () => import('app/modules/packages/package.routes') },
            { path: 'products', data: { breadcrumb: 'Products' }, loadChildren: () => import('app/modules/products/product.routes') },
            { path: 'users', data: { breadcrumb: 'Users' }, loadChildren: () => import('app/modules/users/user.routes') },
            // Admin routes
            { path: 'dashboard', data: { breadcrumb: 'Dashboard' }, loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },
        ]
    }
];
